import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as wellknown from "wellknown";
import { useConfig } from '../../contexts/ConfigContext';
import { useLayers } from '../../contexts/LayerContext';
import { useOptions } from '../../contexts/OptionsContext';
import { useUser } from '../../contexts/UserContext';
import { transformRelationships } from "../../services/mediaEditService";
import { ApiEndpoint, EntityItem, GeoJSONFeature, GeoJSONGeometry, MediaItem, RelatedEntityItem } from "../../types";
import { apiFetch, apiFetchNoResponseRequired, REACT_APP_API_ROOT } from "../../utilities/api";
import { getCategoryColor } from "../../utilities/dataItemUtils";
import { fetchItemWithRelationships } from "../../utilities/dataSourceAdapters";
import { getMediaThumbnailSync } from "../../utilities/authMediaAdapters";
import { constructMediaUrl } from "../../utilities/mediaUtils";
import { extractMediaItems, flattenRelationshipsForDisplay } from "../../utilities/relationshipUtils";
import { handleFormSubmissionError } from "../../utilities/errorHandlers";
import { CMChip } from "../common/CMChip";
import ImageCarousel from "../common/ImageCarousel";
import { PreviewMap } from "../common/PreviewMap";
import RelationshipEditDialog from "../common/RelationshipEditDialog";

interface ItemFormDialogProps {
  open: boolean;
  onClose: () => void;
  endpoint: ApiEndpoint | null;
  mode?: "create" | "edit";
  existingItem?: MediaItem | EntityItem;
  onItemCreated?: (newItem: any) => void;
  onItemUpdated?: (updatedItem: any) => void;
  onItemDeleted?: (deletedItemId: number | string) => void;
}

interface FormData {
  name: string;
  description: string;
  geometry?: string; // GeoJSON as string for editing
}

interface MapData {
  areas: GeoJSONFeature[];
  points: GeoJSONFeature[];
}

// Helper functions for geometry display
const getGeometryDisplayName = (geometry: GeoJSONGeometry | any): string => {
  return geometry?.type || 'Geometry';
};

const getGeometryDescription = (geometry: GeoJSONGeometry | any): string => {
  if (!geometry?.type || !geometry?.coordinates) return 'defined';
  
  switch (geometry.type) {
    case 'MultiPolygon':
      const totalVertices = geometry.coordinates.reduce((total: number, poly: number[][][]) => {
        return total + (poly[0]?.length || 0); // Count vertices in each polygon's outer ring
      }, 0);
      return `${geometry.coordinates.length} polygons, ${totalVertices} total vertices`;
      
    case 'Polygon':
      return `${geometry.coordinates[0]?.length || 0} vertices`;
      
    case 'LineString':
      return `${geometry.coordinates.length} points`;
      
    case 'MultiLineString':
      const totalPoints = geometry.coordinates.reduce((total: number, line: number[][]) => {
        return total + (line?.length || 0);
      }, 0);
      return `${geometry.coordinates.length} lines, ${totalPoints} total points`;
      
    case 'Point':
      return `coordinates: ${geometry.coordinates[1]?.toFixed(6)}, ${geometry.coordinates[0]?.toFixed(6)}`;
      
    case 'MultiPoint':
      return `${geometry.coordinates.length} points`;
      
    default:
      return 'defined';
  }
};

const ItemFormDialog: React.FC<ItemFormDialogProps> = React.memo(({
  open,
  onClose,
  endpoint,
  mode = "create",
  existingItem,
  onItemCreated,
  onItemUpdated,
  onItemDeleted,
  }) => {
  // PERFORMANCE MONITORING: Track what causes excessive re-renders
  const renderCountRef = useRef(0);
  const lastPropsRef = useRef<any>({ open, mode, existingItem });
  
  useEffect(() => {
    renderCountRef.current++;
    const currentProps = { open, mode, existingItem };
    const prevProps = lastPropsRef.current;
    
    const changedProps = [];
    if (prevProps.open !== open) changedProps.push('open');
    if (prevProps.mode !== mode) changedProps.push('mode');
    if (prevProps.existingItem !== existingItem) changedProps.push('existingItem');
    
    if (changedProps.length > 0) {
      console.log(`🔍 [ItemFormDialog] PERF: Render #${renderCountRef.current}, props changed:`, {
        renderCount: renderCountRef.current,
        changedProps: changedProps,
        open: open,
        mode: mode,
        existingItemId: existingItem?.id,
        existingItemIdentity: existingItem === prevProps.existingItem ? 'SAME' : 'DIFFERENT'
      });
    }
    
    lastPropsRef.current = currentProps;
  });

  const { enqueueSnackbar } = useSnackbar();
  const config = useConfig();
  const { refreshLayer } = useLayers();
  const { forceRefreshEndpoint, options, loadOptionsForEndpoint } = useOptions();
  const { getStoredToken, profile, hasWritePermissions } = useUser();
  
  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
    geometry: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Local state to track current item data (for proper relationship display)
  const [currentItem, setCurrentItem] = useState<MediaItem | EntityItem | null>(null);
  
  // Map state
  const [selectedCoordinates, setSelectedCoordinates] = useState<[number, number] | null>(null);
  const [selectedPolygon, setSelectedPolygon] = useState<GeoJSON.Polygon | null>(null);
  const [mapData, setMapData] = useState<MapData>({
    areas: [],
    points: [],
  });
  const [mapSearchTerm, setMapSearchTerm] = useState("");
  const [showEditRelationships, setShowEditRelationships] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedPrimaryImage, setSelectedPrimaryImage] = useState<string | null>(null);
  const [relatedMedia, setRelatedMedia] = useState<any[]>([]);
  const [displayedMediaCount, setDisplayedMediaCount] = useState(20); // Show 20 initially
  const [isInRelationshipUpdate, setIsInRelationshipUpdate] = useState(false);
  const [hasLocalMediaChanges, setHasLocalMediaChanges] = useState(false);
  
  // Dialog states for unlink/delete functionality
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [dialogRelationships, setDialogRelationships] = useState<{
    current: (RelatedEntityItem & { _entityType?: string })[];
    updated: (RelatedEntityItem & { _entityType?: string })[];
  } | null>(null);
  const [deleteMediaItem, setDeleteMediaItem] = useState<MediaItem | null>(null);
  const [mediaItemForUnlink, setMediaItemForUnlink] = useState<MediaItem | null>(null);

  // Check if this endpoint is a geoentity
  const isGeoEntity = endpoint?.entityType === "geo" || 
                     ["places", "family-areas", "trips"].includes(endpoint?.tableName || "");

  // Helper function to initialize form with item data (extracted for reuse)
  const initializeFormWithItem = useCallback((itemToInitialize: MediaItem | EntityItem) => {
    console.log('[ItemFormDialog] initializeFormWithItem called for:', itemToInitialize.name, itemToInitialize.id);
    
    // Initialize form data including geometry for geoentities
    let geometryString = "";
    if (isGeoEntity && itemToInitialize) {
      const geometry = extractGeometryFromItem(itemToInitialize);
      
      if (geometry.coordinates) {
        geometryString = JSON.stringify({
          type: "Point",
          coordinates: geometry.coordinates
        }, null, 2);
      } else if (geometry.polygon) {
        geometryString = JSON.stringify(geometry.polygon, null, 2);
      }
    }
    
    setFormData({
      name: itemToInitialize.name || "",
      description: itemToInitialize.description || "",
      geometry: geometryString,
    });
    setCurrentItem(itemToInitialize);
    console.log('[ItemFormDialog] Form initialized - currentItem set:', itemToInitialize.name, itemToInitialize.id);

    // Extract and set geometry from item if it's a geoentity
    if (isGeoEntity && itemToInitialize) {
      const geometry = extractGeometryFromItem(itemToInitialize);
      console.log('[ItemFormDialog] Extracted geometry from item:', geometry);
      
      if (geometry.coordinates && 
          Array.isArray(geometry.coordinates) && 
          geometry.coordinates.length >= 2 &&
          isFinite(geometry.coordinates[0]) && 
          isFinite(geometry.coordinates[1]) &&
          geometry.coordinates[0] >= -180 && geometry.coordinates[0] <= 180 &&
          geometry.coordinates[1] >= -90 && geometry.coordinates[1] <= 90) {
        setSelectedCoordinates(geometry.coordinates);
        console.log('[ItemFormDialog] Set selectedCoordinates to:', geometry.coordinates);
        setMapData({
          areas: [],
          points: [{
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: geometry.coordinates
            },
            properties: {
              name: itemToInitialize.name || "Current Location",
              id: "existing-point"
            }
          }]
        });
      } else {
        console.log('[ItemFormDialog] Invalid or missing coordinates, skipping geometry setup:', geometry.coordinates);
      }
      
      if (geometry.polygon) {
        setSelectedPolygon(geometry.polygon);
        console.log('[ItemFormDialog] Set selectedPolygon to:', geometry.polygon);
        
        // Determine display name based on geometry type
        const geometryType = (geometry.polygon as any).type;
        const displayName = geometryType === "LineString" ? 
          (itemToInitialize.name || "Current Route") : 
          (itemToInitialize.name || "Current Area");
        const featureId = geometryType === "LineString" ? "existing-route" : "existing-area";
        
        console.log('[ItemFormDialog] Creating mapData.areas with geometry type:', (geometry.polygon as any).type);
        
        const areaFeature = {
          type: "Feature" as const,
          geometry: geometry.polygon as any,
          properties: {
            name: displayName,
            id: featureId
          }
        };
        
        setMapData({
          areas: [areaFeature],
          points: []
        });
      }
    }

    // Extract and set related media for carousel display
    if (itemToInitialize && itemToInitialize.relationships?.media) {
      const mediaItems = extractMediaItems(itemToInitialize.relationships.media).map(item => {
        const finalThumbnailUrl = getRelationshipMediaThumbnailUrl(item);
        
        console.log('[ItemFormDialog] Media item thumbnail generation during init:', {
          itemId: item.id,
          itemName: item.name,
          originalThumbnail: item.entity_thumbnail,
          finalThumbnailUrl
        });
        
        return {
          ...item,
          entity_thumbnail: finalThumbnailUrl
        };
      });
      console.log('[ItemFormDialog] Setting relatedMedia during initialization:', mediaItems.length, 'items');
      setRelatedMedia(mediaItems);
      setDisplayedMediaCount(20); // Reset to show 20 initially
    } else {
      console.log('[ItemFormDialog] No media relationships found during initialization, clearing relatedMedia');
      setRelatedMedia([]);
      setDisplayedMediaCount(20);
    }
  }, [isGeoEntity]);

  // Helper function to get thumbnail URL for relationship media items
  const getRelationshipMediaThumbnailUrl = (mediaItem: any): string => {
    // For relationship media items, we need to construct the proper item structure
    // since extractMediaItems only gives us limited properties
    const itemForThumbnail = {
      id: mediaItem.id,
      entity_thumbnail: mediaItem.entity_thumbnail,
      // Add any other available properties for the adapter pattern
      thumbnail: mediaItem.thumbnail,
      url: mediaItem.url,
      primary_image: mediaItem.primary_image,
      remote_details: mediaItem.remote_details,
      // Copy over any Fulcrum-specific data if present
      fulcrum_photo: mediaItem.fulcrum_photo,
      fulcrum_data: mediaItem.fulcrum_data,
      _source: mediaItem._source
    };
    
    // Use the new Media Resource Adapter pattern
    const thumbnailUrl = getMediaThumbnailSync(itemForThumbnail);
    
    // If that fails, fall back to API endpoint
    if (!thumbnailUrl && mediaItem.id) {
      return constructMediaUrl(`/api/user/media/${mediaItem.id}/thumbnail/`);
    }
    
    return thumbnailUrl || '';
  };

  // Load options for relationship display when dialog opens
  useEffect(() => {
    if (open && mode === "edit" && currentItem?.relationships) {
      console.log('[ItemFormDialog] Loading options for relationship display...');
      
      // Get all endpoints that might be needed for relationship display
      const endpointsToLoad = config.apiEndpoints
        .filter(ep => ep.tableName !== endpoint?.tableName) // Don't load options for the current item's type
        .map(ep => ep.tableName);
      
      console.log('[ItemFormDialog] Endpoints needed for relationships:', endpointsToLoad);
      
      // Check if all required options are already available
      const allOptionsAvailable = endpointsToLoad.every(tableName => 
        options && options[tableName] && options[tableName].length > 0
      );
      
      console.log('[ItemFormDialog] All relationship options available:', allOptionsAvailable);
      console.log('[ItemFormDialog] Current options keys:', Object.keys(options || {}));
      
      if (!allOptionsAvailable && endpointsToLoad.length > 0) {
        // Need to load some options for relationship display
        const missingEndpoints = endpointsToLoad.filter(tableName => 
          !options || !options[tableName] || options[tableName].length === 0
        );
        
        console.log('[ItemFormDialog] Loading missing options for relationship display:', missingEndpoints);
        
        loadOptionsForEndpoint(endpointsToLoad).catch(error => {
          console.error('[ItemFormDialog] Failed to load options for relationships:', error);
        });
      }
    }
  }, [open, mode, currentItem?.relationships, config.apiEndpoints, endpoint?.tableName, options, loadOptionsForEndpoint]);

  // Initialize form data when dialog opens or item changes
  useEffect(() => {
    if (open) {
      if (mode === "edit" && existingItem) {
        // Check if we need to fetch relationships for Django items that don't have them loaded
        const needsRelationships = !existingItem.relationships && endpoint;
        
        if (needsRelationships) {
          console.log('[ItemFormDialog] Django item missing relationships, fetching Level 3 data:', {
            itemId: existingItem.id,
            itemName: existingItem.name,
            endpointName: endpoint.name
          });
          
          // Fetch complete item with relationships using the adapter system
          fetchItemWithRelationships(existingItem.id, endpoint, profile)
            .then(completeItem => {
              console.log('[ItemFormDialog] Successfully fetched complete item with relationships:', {
                itemId: completeItem.id,
                itemName: completeItem.name,
                hasRelationships: !!completeItem.relationships,
                mediaCount: completeItem.relationships?.media?.length || 0
              });
              
              // Initialize form with complete item data
              initializeFormWithItem(completeItem);
            })
            .catch(error => {
              console.error('[ItemFormDialog] Failed to fetch complete item data, using existing item:', error);
              // Fallback to existing item if fetch fails
              initializeFormWithItem(existingItem);
            });
          
          return; // Exit early, initialization will happen in the .then()
        }
        
        // Prevent re-initialization if we already have a currentItem with the same ID
        // AND we have local changes (selectedCoordinates or modified form data)
        // This prevents overwriting local changes when parent updates existingItem after relationship edits
        if (currentItem && currentItem.id === existingItem.id) {
          const hasLocalCoordinateChanges = selectedCoordinates !== null;
          const hasLocalFormChanges = formData.name !== (existingItem.name || "") || 
                                     formData.description !== (existingItem.description || "");
          
          // Check if existingItem has newer data than currentItem
          const existingItemTimestamp = new Date(existingItem.last_updated_at || 0).getTime();
          const currentItemTimestamp = new Date(currentItem.last_updated_at || 0).getTime();
          const existingItemIsNewer = existingItemTimestamp > currentItemTimestamp;
          
          if (hasLocalCoordinateChanges || hasLocalFormChanges || isInRelationshipUpdate || hasLocalMediaChanges || !existingItemIsNewer) {
            console.log('[ItemFormDialog] Skipping re-initialization - preserving local changes for item:', existingItem.id);
            console.log('[ItemFormDialog] hasLocalCoordinateChanges:', hasLocalCoordinateChanges);
            console.log('[ItemFormDialog] hasLocalFormChanges:', hasLocalFormChanges);
            console.log('[ItemFormDialog] isInRelationshipUpdate:', isInRelationshipUpdate);
            console.log('[ItemFormDialog] hasLocalMediaChanges:', hasLocalMediaChanges);
            console.log('[ItemFormDialog] existingItemIsNewer:', existingItemIsNewer);
            console.log('[ItemFormDialog] existingItem timestamp:', existingItem.last_updated_at);
            console.log('[ItemFormDialog] currentItem timestamp:', currentItem.last_updated_at);
            console.log('[ItemFormDialog] selectedCoordinates:', selectedCoordinates);
            return;
          } else {
            console.log('[ItemFormDialog] existingItem has newer data, updating currentItem');
            console.log('[ItemFormDialog] existingItem timestamp:', existingItem.last_updated_at);
            console.log('[ItemFormDialog] currentItem timestamp:', currentItem.last_updated_at);
            console.log('[ItemFormDialog] existingItem relationships:', existingItem.relationships);
            
            // Update related media when we update currentItem with newer data
            if (existingItem.relationships?.media) {
              const mediaItems = extractMediaItems(existingItem.relationships.media).map(item => {
                const finalThumbnailUrl = getRelationshipMediaThumbnailUrl(item);
                
                console.log('[ItemFormDialog] Media item thumbnail generation:', {
                  itemId: item.id,
                  itemName: item.name,
                  originalThumbnail: item.entity_thumbnail,
                  finalThumbnailUrl
                });
                
                return {
                  ...item,
                  entity_thumbnail: finalThumbnailUrl
                };
              });
              console.log('[ItemFormDialog] Total media items with thumbnails:', mediaItems.length);
              console.log('[ItemFormDialog] Setting relatedMedia to (existing item update):', mediaItems);
              setRelatedMedia(mediaItems);
              setDisplayedMediaCount(20); // Reset to show 20 initially
            }
          }
        }
        
        // Standard initialization for items that already have relationships or don't need them
        initializeFormWithItem(existingItem);
      } else {
        // Reset for create mode
        resetForm();
      }
      setMapSearchTerm("");
    }
  }, [open, mode, existingItem, isGeoEntity, initializeFormWithItem, endpoint, profile, fetchItemWithRelationships]);

    // Update related media when currentItem changes (for relationship updates)
  useEffect(() => {
    console.log('[ItemFormDialog] currentItem changed, updating related media');
    console.log('[ItemFormDialog] New currentItem:', currentItem?.name, currentItem?.id);
    console.log('[ItemFormDialog] New currentItem geometry:', (currentItem as any)?.geometry);
    
    if (currentItem && currentItem.relationships?.media && currentItem.relationships.media.length > 0) {
      const mediaItems = extractMediaItems(currentItem.relationships.media).map(item => {
        const finalThumbnailUrl = getRelationshipMediaThumbnailUrl(item);
        
        console.log('[ItemFormDialog] CurrentItem media item thumbnail generation:', {
          itemId: item.id,
          itemName: item.name,
          originalThumbnail: item.entity_thumbnail,
          finalThumbnailUrl
        });
        
        return {
          ...item,
          entity_thumbnail: finalThumbnailUrl
        };
      });
      console.log('[ItemFormDialog] CurrentItem total media items with thumbnails:', mediaItems.length);
      console.log('[ItemFormDialog] Setting relatedMedia to (currentItem update):', mediaItems);
      setRelatedMedia(mediaItems);
      setDisplayedMediaCount(20); // Reset to show 20 initially
    } else if (currentItem) {
      // Clear relatedMedia if currentItem has no media relationships
      console.log('[ItemFormDialog] No media relationships found, clearing relatedMedia');
      setRelatedMedia([]);
      setDisplayedMediaCount(20);
    }
  }, [currentItem]);

  // Debug: Track selectedCoordinates changes
  useEffect(() => {
    console.log('[ItemFormDialog] selectedCoordinates changed to:', selectedCoordinates);
  }, [selectedCoordinates]);

  // Debug: Track relatedMedia changes
  useEffect(() => {
    console.log('[ItemFormDialog] relatedMedia state changed to:', {
      length: relatedMedia.length,
      items: relatedMedia.map(item => ({
        id: item.id,
        name: item.entity_name || item.name,
        entity_thumbnail: item.entity_thumbnail,
        hasThumbnail: !!item.entity_thumbnail
      }))
    });
  }, [relatedMedia]);

  // Strip Z dimensions from coordinates to ensure 2D compatibility with PostGIS
  const stripZDimensions = (geometry: GeoJSONGeometry | any): GeoJSONGeometry | any => {
    if (!geometry || !geometry.type) return geometry;

    const stripCoordinates = (coords: any): any => {
      if (!Array.isArray(coords)) return coords;
      
      if (typeof coords[0] === 'number') {
        // Single coordinate pair - keep only X,Y (first 2 elements)
        return coords.length > 2 ? coords.slice(0, 2) : coords;
      } else if (Array.isArray(coords[0])) {
        // Array of coordinates - recursively strip each
        return coords.map(stripCoordinates);
      }
      return coords;
    };

    const result = { ...geometry };

    // Handle coordinates property
    if (geometry.coordinates) {
      result.coordinates = stripCoordinates(geometry.coordinates);
    }

    // Handle GeometryCollection
    if (geometry.geometries && Array.isArray(geometry.geometries)) {
      result.geometries = geometry.geometries.map(stripZDimensions);
    }

    return result;
  };

  // Extract geometry from existing item (supports points, polygons, and linestrings)
  const extractGeometryFromItem = (item: MediaItem | EntityItem): { 
    coordinates: [number, number] | null; 
    polygon: GeoJSON.Polygon | null 
  } => {
    console.log('[ItemFormDialog] extractGeometryFromItem called for:', item.name, item.id);
    const itemWithGeometry = item as any;
    console.log('[ItemFormDialog] itemWithGeometry.geometry:', itemWithGeometry.geometry);
    console.log('[ItemFormDialog] itemWithGeometry._geometry:', itemWithGeometry._geometry);
    
    const result = { coordinates: null as [number, number] | null, polygon: null as GeoJSON.Polygon | null };
    
    // Try geometry field (WKT or GeoJSON)
    if (itemWithGeometry.geometry) {
      try {
        let geojson;
        if (typeof itemWithGeometry.geometry === 'string') {
          geojson = wellknown.parse(itemWithGeometry.geometry);
        } else {
          geojson = itemWithGeometry.geometry;
        }
        
        if (geojson) {
          if (geojson.type === 'Point' && geojson.coordinates) {
            result.coordinates = [geojson.coordinates[0], geojson.coordinates[1]];
            console.log('[ItemFormDialog] Extracted coordinates from geometry field:', result.coordinates);
          } else if (geojson.type === 'Polygon' && geojson.coordinates) {
            result.polygon = geojson as GeoJSON.Polygon;
            console.log('[ItemFormDialog] Extracted polygon from geometry field:', result.polygon);
          } else if (geojson.type === 'MultiPolygon' && geojson.coordinates) {
            // Store MultiPolygon as polygon for display compatibility
            result.polygon = geojson as any;
            console.log('[ItemFormDialog] Extracted multipolygon from geometry field:', result.polygon);
          } else if (geojson.type === 'LineString' && geojson.coordinates) {
            // Store LineString as polygon for editing compatibility 
            result.polygon = geojson as any;
            console.log('[ItemFormDialog] Extracted linestring from geometry field:', result.polygon);
          } else if (geojson.type === 'MultiLineString' && geojson.coordinates) {
            // Store MultiLineString as polygon for display compatibility
            result.polygon = geojson as any;
            console.log('[ItemFormDialog] Extracted multilinestring from geometry field:', result.polygon);
          }
        }
      } catch (error) {
        console.warn('[ItemFormDialog] Error parsing geometry:', error);
      }
    }

    // Try _geometry field if no geometry found yet
    if (!result.coordinates && !result.polygon && itemWithGeometry._geometry) {
      try {
        let geojson;
        if (typeof itemWithGeometry._geometry === 'string') {
          geojson = wellknown.parse(itemWithGeometry._geometry);
        } else {
          geojson = itemWithGeometry._geometry;
        }
        
        if (geojson) {
          if (geojson.type === 'Point' && geojson.coordinates) {
            result.coordinates = [geojson.coordinates[0], geojson.coordinates[1]];
            console.log('[ItemFormDialog] Extracted coordinates from _geometry field:', result.coordinates);
          } else if (geojson.type === 'Polygon' && geojson.coordinates) {
            result.polygon = geojson as GeoJSON.Polygon;
            console.log('[ItemFormDialog] Extracted polygon from _geometry field:', result.polygon);
          } else if (geojson.type === 'MultiPolygon' && geojson.coordinates) {
            // Store MultiPolygon as polygon for display compatibility
            result.polygon = geojson as any;
            console.log('[ItemFormDialog] Extracted multipolygon from _geometry field:', result.polygon);
          } else if (geojson.type === 'LineString' && geojson.coordinates) {
            // Store LineString as polygon for editing compatibility
            result.polygon = geojson as any;
            console.log('[ItemFormDialog] Extracted linestring from _geometry field:', result.polygon);
          } else if (geojson.type === 'MultiLineString' && geojson.coordinates) {
            // Store MultiLineString as polygon for display compatibility
            result.polygon = geojson as any;
            console.log('[ItemFormDialog] Extracted multilinestring from _geometry field:', result.polygon);
          }
        }
      } catch (error) {
        console.warn('[ItemFormDialog] Error parsing _geometry:', error);
      }
    }

    if (!result.coordinates && !result.polygon) {
      console.log('[ItemFormDialog] No geometry found in item, returning null');
    }
    
    return result;
  };

  // Reset form to initial state
  const resetForm = () => {
    console.log('[ItemFormDialog] resetForm called - clearing all state');
    console.log('[ItemFormDialog] Previous selectedCoordinates:', selectedCoordinates);
    console.log('[ItemFormDialog] Previous selectedPolygon:', selectedPolygon);
    setFormData({ name: "", description: "", geometry: "" });
    setSelectedCoordinates(null);
    setSelectedPolygon(null);
    setMapData({ areas: [], points: [] });
    setMapSearchTerm("");
    setCurrentItem(null);
    setIsInRelationshipUpdate(false);
    setSelectedPrimaryImage(null);
    setHasLocalMediaChanges(false);
  };

  // Handle form field changes
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // If geometry field is being updated, try to parse and update map
    if (field === 'geometry' && isGeoEntity && value.trim()) {
      handleGeometryChange(value);
    }
  };

  // Handle geometry changes from text input
  const handleGeometryChange = (geometryString: string) => {
    try {
      if (!geometryString.trim()) {
        // Clear geometry
        setSelectedCoordinates(null);
        setSelectedPolygon(null);
        setMapData({ areas: [], points: [] });
        return;
      }

      const geometry = JSON.parse(geometryString);
      
      // Handle Feature Collections
      if (geometry.type === 'FeatureCollection' && geometry.features && Array.isArray(geometry.features)) {
        // For Feature Collections, extract all geometries and display them
        const allGeometries = geometry.features
          .map((feature: any) => feature.geometry)
          .filter((geom: any) => geom && geom.type && geom.coordinates);
        
        if (allGeometries.length === 0) {
          throw new Error('FeatureCollection contains no valid geometries');
        }
        
        // Create map features for all geometries in the collection
        const mapFeatures = geometry.features.map((feature: any, index: number) => ({
          type: "Feature" as const,
          geometry: feature.geometry,
          properties: {
            name: feature.properties?.name || `Feature ${index + 1}`,
            id: `feature-${index}`,
            ...feature.properties
          }
        }));
        
        // Clear coordinates and set all features as areas
        setSelectedCoordinates(null);
        setSelectedPolygon(geometry); // Store the whole FeatureCollection
        setMapData({
          areas: mapFeatures,
          points: []
        });
        
        console.log('[ItemFormDialog] Loaded FeatureCollection with', allGeometries.length, 'features');
        return;
      }
      
      // Handle individual Features
      if (geometry.type === 'Feature' && geometry.geometry) {
        // Extract the geometry from the Feature
        const actualGeometry = geometry.geometry;
        setSelectedCoordinates(null);
        setSelectedPolygon(actualGeometry);
        setMapData({
          areas: [{
            type: "Feature",
            geometry: actualGeometry,
            properties: {
              name: geometry.properties?.name || "Feature Geometry",
              id: "feature-geometry",
              ...geometry.properties
            }
          }],
          points: []
        });
        return;
      }
      
      // Validate basic GeoJSON Geometry structure
      if (!geometry.type || !geometry.coordinates) {
        throw new Error('Invalid GeoJSON: missing type or coordinates');
      }

      // Handle different geometry types
      if (geometry.type === 'Point' && Array.isArray(geometry.coordinates) && geometry.coordinates.length >= 2) {
        const coords: [number, number] = [geometry.coordinates[0], geometry.coordinates[1]];
        setSelectedCoordinates(coords);
        setSelectedPolygon(null);
        
        // Update map data
        setMapData({
          areas: [],
          points: [{
            type: "Feature",
            geometry: geometry,
            properties: {
              name: formData.name || "Edited Location",
              id: "edited-point"
            }
          }]
        });
      } else if (['Polygon', 'MultiPolygon', 'LineString', 'MultiLineString'].includes(geometry.type)) {
        setSelectedCoordinates(null);
        setSelectedPolygon(geometry);
        
        // Determine display name based on geometry type
        const displayName = ['LineString', 'MultiLineString'].includes(geometry.type) ? 
          (formData.name || "Edited Route") : 
          (formData.name || "Edited Area");
        const featureId = ['LineString', 'MultiLineString'].includes(geometry.type) ? "edited-route" : "edited-area";
        
        // Update map data
        setMapData({
          areas: [{
            type: "Feature",
            geometry: geometry,
            properties: {
              name: displayName,
              id: featureId
            }
          }],
          points: []
        });
      } else {
        throw new Error(`Unsupported geometry type: ${geometry.type}`);
      }
      
      console.log('[ItemFormDialog] Geometry updated from text input:', geometry.type);
    } catch (error) {
      console.warn('[ItemFormDialog] Invalid GeoJSON in geometry field:', error);
      // Don't clear the map on invalid JSON - let user continue editing
    }
  };

  // Map click handler to set coordinates (only for point geometries)
  const handleMapClick = useCallback((event: any) => {
    if (!isGeoEntity || endpoint?.tableName !== "places") return;
    
    const { lng, lat } = event.lngLat;
    const coordinates: [number, number] = [lng, lat];
    console.log('[ItemFormDialog] Map clicked, setting coordinates:', coordinates);
    console.log('[ItemFormDialog] Previous selectedCoordinates:', selectedCoordinates);
    setSelectedCoordinates(coordinates);
    
    console.log('[ItemFormDialog] Map clicked, coordinates set:', coordinates);
    
    // Update geometry text field
    const pointGeometry = {
      type: "Point" as const,
      coordinates: coordinates as [number, number]
    };
    
    setFormData(prev => ({
      ...prev,
      geometry: JSON.stringify(pointGeometry, null, 2)
    }));
    
    // Update map data to show the selected point
    setMapData({
      areas: [],
      points: [{
        type: "Feature",
        geometry: pointGeometry,
        properties: {
          name: mode === "edit" ? "Updated Location" : "Selected Location",
          id: "temp-point"
        }
      }]
    });
  }, [isGeoEntity, endpoint?.tableName, mode, selectedCoordinates]);

  // Handle coordinate search (fly to location)
  const handleCoordinateSearch = useCallback(async (searchTerm: string) => {
    console.log('[ItemFormDialog] Coordinate search:', searchTerm);
    
    // Simple coordinate parsing (latitude, longitude)
    const coordinateRegex = /^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/;
    const match = searchTerm.trim().match(coordinateRegex);
    
    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      
      if (lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90) {
        const coordinates: [number, number] = [lng, lat];
        setSelectedCoordinates(coordinates);
        
        // Update geometry text field
        const pointGeometry = {
          type: "Point" as const,
          coordinates: coordinates as [number, number]
        };
        
        setFormData(prev => ({
          ...prev,
          geometry: JSON.stringify(pointGeometry, null, 2)
        }));
        
        // Update map data
        setMapData({
          areas: [],
          points: [{
            type: "Feature",
            geometry: pointGeometry,
            properties: {
              name: "Searched Location",
              id: "searched-point"
            }
          }]
        });
        
        enqueueSnackbar(`Moved to coordinates: ${lat}, ${lng}`, { variant: "success" });
        return [];
      }
    }
    
    enqueueSnackbar("Invalid coordinates. Use format: latitude, longitude (e.g., -25.2744, 133.7751)", { 
      variant: "warning" 
    });
    return [];
  }, [enqueueSnackbar]);

  // Clear coordinates
  const handleClearCoordinates = () => {
    setSelectedCoordinates(null);
    setMapData({ areas: [], points: [] });
    setFormData(prev => ({
      ...prev,
      geometry: ""
    }));
  };


  // Function to determine if a color is dark (for text contrast)
  const isColorDark = (hexColor: string) => {
    try {
      // Convert hex to RGB
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      
      // Calculate brightness based on YIQ formula
      return (r * 299 + g * 587 + b * 114) / 1000 < 128;
    } catch (error) {
      // Default to assuming it's dark
      return true;
    }
  };

  // Render relationships function (similar to SingleItemPreview) - EXCLUDING media
  const renderRelationships = () => {
    console.log('[ItemFormDialog] renderRelationships called');
    console.log('[ItemFormDialog] currentItem?.relationships:', currentItem?.relationships);
    
    if (!currentItem?.relationships) {
      console.log('[ItemFormDialog] No relationships found - returning placeholder');
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No relationships defined
        </Typography>
      );
    }
    
    // Use utility function to flatten relationships for display
    const allRelationshipItems = flattenRelationshipsForDisplay(currentItem.relationships, config);
    
    console.log('[ItemFormDialog] Total relationship items found:', allRelationshipItems.length);
    
    // If no relationships found after processing, show placeholder
    if (allRelationshipItems.length === 0) {
      console.log('[ItemFormDialog] No relationship items found after processing - returning placeholder');
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No relationships found
        </Typography>
      );
    }
    
    // Render all relationships as chips in a single container
    return (
      <Box sx={{ 
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        justifyContent: 'flex-start'
      }}>
        {allRelationshipItems.map(item => {
          const bgColor = getCategoryColor(item.category);
          const textColor = isColorDark(bgColor) ? '#ffffff' : '#000000';
          
          return (
            <CMChip
              key={`${item.key}-${item.id}`}
              label={item.name}
              size="small"
              sx={{ 
                height: "18px", 
                fontSize: "0.65rem", 
                bgcolor: bgColor,
                color: textColor,
                "& .MuiChip-label": { 
                  px: 0.75,
                  py: 0
                } 
              }}
            />
          );
        })}
      </Box>
    );
  };

  // Handle Edit Relationships button click
  const handleEditRelationships = () => {
    if (!currentItem) return;
    console.log('[ItemFormDialog] Opening RelationshipEditDialog - setting isInRelationshipUpdate flag');
    setIsInRelationshipUpdate(true);
    setShowEditRelationships(true);
  };

  // Helper function to check if an item has relationships
  const hasRelationships = (relationships: {
    related_entities?: Record<string, any[]>;
    geo_entities?: Record<string, any[]>;
    media?: any[];
  } | undefined): boolean => {
    if (!relationships) return false;

    // Check related_entities
    if (relationships.related_entities && 
        Object.values(relationships.related_entities).some(arr => 
          Array.isArray(arr) && arr.length > 0
        )) {
      return true;
    }

    // Check geo_entities
    if (relationships.geo_entities && 
        Object.values(relationships.geo_entities).some(arr => 
          Array.isArray(arr) && arr.length > 0
        )) {
      return true;
    }

    // Check media
    if (Array.isArray(relationships.media) && relationships.media.length > 0) {
      return true;
    }

    return false;
  };

  // Memoize autoZoom calculation
  const shouldAutoZoom = useMemo(() => {
    return mapData.points.length > 0 || mapData.areas.length > 0;
  }, [mapData.points.length, mapData.areas.length]);

  // Memoize initial view state
  const initialViewState = useMemo(() => ({
    longitude: config.initialMapView?.longitude || 133.7751,
    latitude: config.initialMapView?.latitude || -25.2744,
    zoom: config.initialMapView?.zoom || 4,
  }), [config.initialMapView]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!endpoint || !formData.name.trim()) {
      enqueueSnackbar("Name is required", { variant: "error" });
      return;
    }

    // For geoentities, check if geometry is required based on entity type
    if (isGeoEntity) {
      if (endpoint.tableName === "places" && !selectedCoordinates) {
        enqueueSnackbar("Please set a location by clicking on the map", { variant: "error" });
        return;
      }
      // Family-areas and trips don't require geometry - they can be created without geometry and added later via other tools
    }

    try {
      setIsSubmitting(true);

      let url: string;
      let method: string;
      
      if (mode === "edit" && currentItem) {
        url = `${REACT_APP_API_ROOT}${endpoint.api_endpoint}${currentItem.id}/`;
        method = "PATCH";
      } else {
        url = `${REACT_APP_API_ROOT}${endpoint.api_endpoint}`;
        method = "POST";
      }
      
      const payload: any = {
        name: formData.name.trim(),
      };

      // Only add description if it's not empty
      if (formData.description.trim()) {
        payload.description = formData.description.trim();
      }

      // Add geometry for geoentities
      if (isGeoEntity && formData.geometry && formData.geometry.trim()) {
        try {
          const geometry = JSON.parse(formData.geometry);
          
          let finalGeometry = geometry;
          
          // Convert FeatureCollection to single geometry (PostGIS doesn't support FeatureCollections)
          if (geometry.type === 'FeatureCollection' && geometry.features && Array.isArray(geometry.features)) {
            if (geometry.features.length === 0) {
              throw new Error('FeatureCollection is empty');
            }
            
            if (geometry.features.length === 1) {
              // Single feature - extract its geometry
              finalGeometry = geometry.features[0].geometry;
              console.log('[ItemFormDialog] Extracted single geometry from FeatureCollection:', finalGeometry.type);
            } else {
              // Multiple features - try to combine intelligently based on geometry types
              const geometries = geometry.features.map((feature: any) => feature.geometry);
              const geometryTypes = [...new Set(geometries.map((g: any) => g.type))];
              
              // If all geometries are the same type, try to combine them into a Multi* type
              if (geometryTypes.length === 1) {
                const singleType = geometryTypes[0];
                
                if (singleType === 'LineString') {
                  // Combine LineStrings into MultiLineString
                  finalGeometry = {
                    type: "MultiLineString",
                    coordinates: geometries.map((g: any) => g.coordinates)
                  };
                  console.log('[ItemFormDialog] Combined', geometries.length, 'LineStrings into MultiLineString');
                } else if (singleType === 'Polygon') {
                  // Combine Polygons into MultiPolygon
                  finalGeometry = {
                    type: "MultiPolygon",
                    coordinates: geometries.map((g: any) => g.coordinates)
                  };
                  console.log('[ItemFormDialog] Combined', geometries.length, 'Polygons into MultiPolygon');
                } else if (singleType === 'Point') {
                  // Combine Points into MultiPoint
                  finalGeometry = {
                    type: "MultiPoint",
                    coordinates: geometries.map((g: any) => g.coordinates)
                  };
                  console.log('[ItemFormDialog] Combined', geometries.length, 'Points into MultiPoint');
                } else if (singleType === 'MultiLineString') {
                  // Flatten MultiLineStrings into a single MultiLineString
                  const allCoordinates = geometries.flatMap((g: any) => g.coordinates);
                  finalGeometry = {
                    type: "MultiLineString",
                    coordinates: allCoordinates
                  };
                  console.log('[ItemFormDialog] Flattened', geometries.length, 'MultiLineStrings into single MultiLineString with', allCoordinates.length, 'line segments');
                } else if (singleType === 'MultiPolygon') {
                  // Flatten MultiPolygons into a single MultiPolygon
                  const allCoordinates = geometries.flatMap((g: any) => g.coordinates);
                  finalGeometry = {
                    type: "MultiPolygon",
                    coordinates: allCoordinates
                  };
                  console.log('[ItemFormDialog] Flattened', geometries.length, 'MultiPolygons into single MultiPolygon');
                } else if (singleType === 'MultiPoint') {
                  // Flatten MultiPoints into a single MultiPoint
                  const allCoordinates = geometries.flatMap((g: any) => g.coordinates);
                  finalGeometry = {
                    type: "MultiPoint",
                    coordinates: allCoordinates
                  };
                  console.log('[ItemFormDialog] Flattened', geometries.length, 'MultiPoints into single MultiPoint');
                } else {
                  // For other types, fall back to GeometryCollection
                  finalGeometry = {
                    type: "GeometryCollection",
                    geometries: geometries
                  };
                  console.log('[ItemFormDialog] Combined', geometries.length, 'complex geometries into GeometryCollection');
                }
              } else {
                // Mixed geometry types - use GeometryCollection
                finalGeometry = {
                  type: "GeometryCollection",
                  geometries: geometries
                };
                console.log('[ItemFormDialog] Combined', geometries.length, 'mixed geometry types into GeometryCollection');
              }
            }
          }
          // Extract geometry from Feature wrapper
          else if (geometry.type === 'Feature' && geometry.geometry) {
            finalGeometry = geometry.geometry;
            console.log('[ItemFormDialog] Extracted geometry from Feature:', finalGeometry.type);
          }
          
          // Validate final geometry structure
          const isValidGeometry = 
            (finalGeometry.type && finalGeometry.coordinates && 
             ['Point', 'LineString', 'Polygon', 'MultiLineString', 'MultiPolygon'].includes(finalGeometry.type)) ||
            (finalGeometry.type === 'GeometryCollection' && finalGeometry.geometries);
          
          if (isValidGeometry) {
            // Strip Z dimensions to ensure 2D compatibility with PostGIS
            const geometry2D = stripZDimensions(finalGeometry);
            payload.geometry = geometry2D;
            console.log('[ItemFormDialog] Adding geometry to payload:', geometry2D.type);
          } else {
            throw new Error('Invalid geometry structure after processing');
          }
        } catch (error) {
          console.warn('[ItemFormDialog] Invalid geometry JSON, skipping:', error);
          enqueueSnackbar("Invalid geometry format. Please check your GeoJSON.", { variant: "warning" });
          return; // Don't submit with invalid geometry
        }
      } else if (isGeoEntity && endpoint.tableName === "places" && selectedCoordinates) {
        // Fallback for places using the old coordinate system
        payload.geometry = {
          type: "Point",
          coordinates: selectedCoordinates
        };
      }

      // Add relationships if they exist (edit mode with updated relationships)
      if (mode === "edit" && currentItem?.relationships) {
        // Ensure relationships have proper structure for transformRelationships
        const relationshipsToTransform = {
          related_entities: currentItem.relationships.related_entities || {},
          geo_entities: currentItem.relationships.geo_entities || {},
          media: currentItem.relationships.media || []
        };
        
        const transformedRelationships = transformRelationships(relationshipsToTransform, config);
        if (transformedRelationships) {
          payload.relationships = transformedRelationships;
        }
      }

      // Add primary image if one was selected
      if (selectedPrimaryImage && mode === "edit") {
        payload.primary_image_write = {
          id: parseInt(selectedPrimaryImage)
        };
      }

      const response = await apiFetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log(`[ItemFormDialog] Item ${mode === 'edit' ? 'updated' : 'created'} successfully:`, response);

      // Refresh OptionsContext for created/updated items (so changes appear in dropdowns immediately)
      if (mode === "create" || mode === "edit") {
        console.log(`[ItemFormDialog] Refreshing options for ${endpoint.tableName} after ${mode}`);
        try {
          await forceRefreshEndpoint(endpoint.tableName);
          console.log(`[ItemFormDialog] Options refreshed successfully for ${endpoint.tableName}`);
        } catch (optionsError) {
          console.warn(`[ItemFormDialog] Failed to refresh options for ${endpoint.tableName}:`, optionsError);
          // Don't fail the operation if options refresh fails
        }
      }

      // Notify parent component
      if (mode === "edit" && onItemUpdated) {
        onItemUpdated(response);
      } else if (mode === "create" && onItemCreated) {
        onItemCreated(response);
      }

      // Update local state for edit mode
      if (mode === "edit") {
        setCurrentItem(response);
        // Clear selected primary image after successful update
        setSelectedPrimaryImage(null);
        // Clear local media changes flag after successful update
        setHasLocalMediaChanges(false);
      }

      enqueueSnackbar(`${endpoint.name.slice(0, -1)} ${mode === 'edit' ? 'updated' : 'created'} successfully`, { 
        variant: "success" 
      });
      
      // Refresh the map layer if this is a geo entity with a corresponding map layer
      // Add delay to handle race condition with backend cache invalidation
      if (isGeoEntity) {
        const mapLayer = config.mapLayers?.find(layer => 
          (layer.id === 'Dambi Places' && endpoint.tableName === 'places') ||
          (layer.id === 'Dambeema' && endpoint.tableName === 'family-areas') ||
          (layer.id === 'Trips' && endpoint.tableName === 'trips')
        );
        
        if (mapLayer) {
          console.log(`[ItemFormDialog] Scheduling map layer refresh: ${mapLayer.id}`);
          
          // Wait for backend cache invalidation to complete before refreshing
          // This prevents race condition where layer refresh happens before cache is cleared
          setTimeout(() => {
            console.log(`[ItemFormDialog] Refreshing map layer: ${mapLayer.id}`);
            refreshLayer(mapLayer.id);
          }, 750); // 750ms delay to ensure transaction.on_commit() cache invalidation completes
        }
      }

      // Reset form and close dialog
      resetForm();
      onClose();
    } catch (error) {
      console.error(`[ItemFormDialog] Error ${mode === 'edit' ? 'updating' : 'creating'} item:`, error);
      
      handleFormSubmissionError(error, enqueueSnackbar, {
        action: mode === 'edit' ? 'edit' : 'create',
        itemType: endpoint?.name || 'items'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  // Handle item deletion
  const handleDelete = async () => {
    if (!endpoint || !currentItem || mode !== "edit") return;

    try {
      setIsDeleting(true);

      const url = `${REACT_APP_API_ROOT}${endpoint.api_endpoint}${currentItem.id}/`;
      
      await apiFetchNoResponseRequired(url, {
        method: "DELETE",
      });

      console.log(`[ItemFormDialog] Item deleted successfully:`, currentItem.id);

      // Refresh OptionsContext to remove deleted item from dropdowns
      console.log(`[ItemFormDialog] Refreshing options for ${endpoint.tableName} after deletion`);
      try {
        await forceRefreshEndpoint(endpoint.tableName);
        console.log(`[ItemFormDialog] Options refreshed successfully for ${endpoint.tableName}`);
      } catch (optionsError) {
        console.warn(`[ItemFormDialog] Failed to refresh options for ${endpoint.tableName}:`, optionsError);
        // Don't fail the deletion if options refresh fails
      }

      // Notify parent component
      if (onItemDeleted) {
        onItemDeleted(currentItem.id);
      }

      enqueueSnackbar(`${endpoint.name.slice(0, -1)} deleted successfully`, { 
        variant: "success" 
      });
      
      // Close dialogs
      setShowDeleteDialog(false);
      onClose();
    } catch (error) {
      console.error(`[ItemFormDialog] Error deleting item:`, error);
      
      handleFormSubmissionError(error, enqueueSnackbar, {
        action: 'delete',
        itemType: endpoint?.name || 'items'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle set primary image (local state only - actual update happens in form submission)
  const handleSetPrimaryImage = () => {
    if (!selectedPrimaryImage) return;

    enqueueSnackbar("Primary image will be set when you click Update", {
      variant: "info",
    });
    
    // Keep the selected primary image ID for the final form submission
    // selectedPrimaryImage state is already set, so the visual selection remains
  };

  const handleSelectImage = (item: any) => {
    setSelectedPrimaryImage(item.id.toString());
  };

  const handleLoadMoreMedia = () => {
    setDisplayedMediaCount(prev => Math.min(prev + 20, relatedMedia.length));
  };

  // Prepare unlink media (similar to DataItemDetails.tsx)
  const prepareUnlink = async (mediaId: number) => {
    try {
      // Find the media endpoint from config
      const mediaEndpoint = config.apiEndpoints.find(endpoint => endpoint.tableName === 'media');
      if (!mediaEndpoint) {
        throw new Error('Media endpoint not found in configuration');
      }

      // Fetch the current media item with relationships using the generalized system
      const mediaItem = await fetchItemWithRelationships(mediaId, mediaEndpoint, profile);

      console.log('[ItemFormDialog] Media item relationships:', mediaItem.relationships);
      console.log('[ItemFormDialog] Current endpoint relationship key:', endpoint?.relationshipKey);
      console.log('[ItemFormDialog] Current item ID:', currentItem?.id);

      // Show ALL relationships this media item has, not just the current endpoint type
      let allCurrentRels: (RelatedEntityItem & { _entityType?: string })[] = [];
      
      // Collect all relationships from all sections, preserving entity type information
      if (mediaItem.relationships) {
        // Add all related_entities relationships
        if (mediaItem.relationships.related_entities) {
          Object.entries(mediaItem.relationships.related_entities).forEach(([key, entities]) => {
            const entitiesWithType = (entities as RelatedEntityItem[]).map(entity => ({
              ...entity,
              _entityType: key // Preserve the entity type for filtering
            }));
            allCurrentRels.push(...entitiesWithType);
          });
        }
        
        // Add all geo_entities relationships
        if (mediaItem.relationships.geo_entities) {
          Object.entries(mediaItem.relationships.geo_entities).forEach(([key, entities]) => {
            const entitiesWithType = (entities as RelatedEntityItem[]).map(entity => ({
              ...entity,
              _entityType: key // Preserve the entity type for filtering
            }));
            allCurrentRels.push(...entitiesWithType);
          });
        }
        
        // Add media relationships if they exist
        if (mediaItem.relationships.media && Array.isArray(mediaItem.relationships.media)) {
          const mediaWithType = mediaItem.relationships.media.map((media: any) => ({
            ...media,
            _entityType: 'media' // Media entities
          }));
          allCurrentRels.push(...mediaWithType);
        }
      }

      console.log('[ItemFormDialog] Found ALL current relationships:', allCurrentRels);
      console.log('[ItemFormDialog] Total relationship count:', allCurrentRels.length);

      // Debug: Show what we're filtering for
      console.log('[ItemFormDialog] Filtering for:');
      console.log('[ItemFormDialog] - Current item ID:', currentItem?.id);
      console.log('[ItemFormDialog] - Current item name:', currentItem?.name);
      console.log('[ItemFormDialog] - Endpoint tableName:', endpoint?.tableName);
      console.log('[ItemFormDialog] - Endpoint relationshipKey:', endpoint?.relationshipKey);

      // Filter out the relationship to be removed (the current item)
      // We need to match both ID and entity type to be truly correct
      const updatedRels = allCurrentRels.filter(
        (relationship) => {
          const isSameId = relationship.id === currentItem?.id;
          
          // Check if the entity type matches the current endpoint
          const isSameEntityType = relationship._entityType === endpoint?.relationshipKey ||
                                  relationship._entityType === endpoint?.tableName ||
                                  // Handle special case for media entities
                                  (relationship._entityType === 'media' && endpoint?.tableName === 'media');
          
          const willBeRemoved = isSameId && isSameEntityType;
          
          // Debug: Log each relationship's decision
          console.log('[ItemFormDialog] Relationship:', {
            name: relationship.entity_name || relationship.name,
            id: relationship.id,
            entityType: relationship._entityType,
            isSameId,
            isSameEntityType,
            willBeRemoved
          });
          
          // Only remove if both ID and entity type match (most precise filtering)
          return !willBeRemoved;
        }
      );

      console.log('[ItemFormDialog] Updated relationships after filtering out current item:', updatedRels);
      console.log('[ItemFormDialog] Relationships remaining after unlink:', updatedRels.length);

      setDialogRelationships({
        current: allCurrentRels,
        updated: updatedRels,
      });
      
      // Store the complete media item for sending the full relationship update
      setMediaItemForUnlink(mediaItem);
      setUnlinkDialogOpen(true);
    } catch (error) {
      console.error("Error preparing unlink:", error);
      
      // Handle specific error cases
      if (error instanceof Error && error.message && error.message.includes('404')) {
        enqueueSnackbar("Media item no longer exists (may have been deleted)", {
          variant: "warning",
        });
        
        // Remove from local state since it's already deleted
        const deletedMediaId = parseInt(selectedPrimaryImage || '0');
        setRelatedMedia((prevMedia) =>
          prevMedia.filter((media) => media.id !== deletedMediaId)
        );
        
        // Clear selection
        setSelectedPrimaryImage(null);
      } else {
        enqueueSnackbar("Failed to prepare unlink operation", {
          variant: "error",
        });
      }
    }
  };

  // Handle unlink media
  const handleUnlinkMedia = async () => {
    if (!selectedPrimaryImage || !endpoint || !dialogRelationships || !mediaItemForUnlink) return;

    try {
      const apiRoot = REACT_APP_API_ROOT?.replace(/\/$/, "");

      // Create the complete updated relationships by copying all existing relationships
      // and removing only the specific relationship we're unlinking
      const updatedRelationships = { ...mediaItemForUnlink.relationships };
      
      // Update the specific relationship type to remove the current item
      const relationshipKey = endpoint.relationshipKey!;
      if (updatedRelationships.related_entities && updatedRelationships.related_entities[relationshipKey]) {
        updatedRelationships.related_entities[relationshipKey] = dialogRelationships.updated.map(rel => ({
          id: rel.id,
          description: rel.rel_description || '',
        }));
      }

      // Create form data using the correct backend format
      const formData = new FormData();
      formData.append(
        "relationships",
        JSON.stringify(updatedRelationships)
      );

      // Send the PATCH request
      const response = await apiFetch(
        `${apiRoot}/api/user/media/${selectedPrimaryImage}/`,
        {
          method: "PATCH",
          body: formData, // Send as form data instead of JSON
        }
      );

      setUnlinkDialogOpen(false);

      const unlinkedMediaId = parseInt(selectedPrimaryImage);

      // CRITICAL: Update the currentItem's relationships to remove the unlinked media
      // This is the single source of truth - let the useEffect handle updating relatedMedia
      if (currentItem?.relationships?.media) {
        const updatedCurrentItem = {
          ...currentItem,
          relationships: {
            ...currentItem.relationships,
            media: currentItem.relationships.media.filter(
              (media: any) => media.id !== unlinkedMediaId
            )
          }
        };
        setCurrentItem(updatedCurrentItem);
        
        // Notify parent component of the change
        if (onItemUpdated) {
          onItemUpdated(updatedCurrentItem);
        }
      } else {
        // If no relationships exist, just update the relatedMedia state directly
        setRelatedMedia((prevMedia) =>
          prevMedia.filter((media) => media.id !== unlinkedMediaId)
        );
      }

      // Clear selection
      setSelectedPrimaryImage(null);

      // Set flag to indicate we have local media changes
      setHasLocalMediaChanges(true);

      enqueueSnackbar("Media unlinked successfully", { variant: "success" });
    } catch (error) {
      console.error("Error unlinking media:", error);
      enqueueSnackbar("Failed to unlink media", { variant: "error" });
    }
  };

  // Prepare delete media
  const prepareDelete = async (mediaId: number) => {
    try {
      console.log('[ItemFormDialog] prepareDelete called for media ID:', mediaId);
      
      // Find the media endpoint from config
      const mediaEndpoint = config.apiEndpoints.find(endpoint => endpoint.tableName === 'media');
      if (!mediaEndpoint) {
        throw new Error('Media endpoint not found in configuration');
      }

      // Fetch the media item with relationships using the generalized system
      const mediaItem = (await fetchItemWithRelationships(mediaId, mediaEndpoint, profile)) as MediaItem;

      console.log('[ItemFormDialog] Media item fetched for delete:', mediaItem);
      console.log('[ItemFormDialog] Media item relationships:', mediaItem.relationships);
      
      // Log the relationships structure for debugging
      if (mediaItem.relationships) {
        console.log('[ItemFormDialog] Related entities:', mediaItem.relationships.related_entities);
        console.log('[ItemFormDialog] Geo entities:', mediaItem.relationships.geo_entities);
        console.log('[ItemFormDialog] Media relationships:', mediaItem.relationships.media);
      }

      setDeleteMediaItem(mediaItem);
      setDeleteDialogOpen(true);
    } catch (error) {
      console.error("Error preparing delete:", error);
      
      // Handle specific error cases
      if (error instanceof Error && error.message && error.message.includes('404')) {
        enqueueSnackbar("Media item no longer exists (may have been deleted)", {
          variant: "warning",
        });
        
        // Remove from local state since it's already deleted
        const deletedMediaId = parseInt(selectedPrimaryImage || '0');
        setRelatedMedia((prevMedia) =>
          prevMedia.filter((media) => media.id !== deletedMediaId)
        );
        
        // Clear selection
        setSelectedPrimaryImage(null);
      } else {
        enqueueSnackbar("Failed to prepare delete operation", {
          variant: "error",
        });
      }
    }
  };

  // Handle delete media
  const handleDeleteMedia = async () => {
    if (!selectedPrimaryImage) return;

    try {
      const apiRoot = REACT_APP_API_ROOT?.replace(/\/$/, "");
      await apiFetchNoResponseRequired(
        `${apiRoot}/api/user/media/${selectedPrimaryImage}/`,
        {
          method: "DELETE",
        }
      );

      setDeleteDialogOpen(false);
      
      const deletedMediaId = parseInt(selectedPrimaryImage);
      
      // CRITICAL: Update the currentItem's relationships to remove the deleted media
      // This is the single source of truth - let the useEffect handle updating relatedMedia
      if (currentItem?.relationships?.media) {
        const updatedCurrentItem = {
          ...currentItem,
          relationships: {
            ...currentItem.relationships,
            media: currentItem.relationships.media.filter(
              (media: any) => media.id !== deletedMediaId
            )
          }
        };
        setCurrentItem(updatedCurrentItem);
        
        // Notify parent component of the change
        if (onItemUpdated) {
          onItemUpdated(updatedCurrentItem);
        }
      } else {
        // If no relationships exist, just update the relatedMedia state directly
        setRelatedMedia((prevMedia) =>
          prevMedia.filter((media) => media.id !== deletedMediaId)
        );
      }

      // Clear selection
      setSelectedPrimaryImage(null);

      // Set flag to indicate we have local media changes
      setHasLocalMediaChanges(true);

      enqueueSnackbar("Media deleted successfully", { variant: "success" });
    } catch (error) {
      console.error("Error deleting media:", error);
      enqueueSnackbar("Failed to delete media", { variant: "error" });
    }
  };

  if (!endpoint) {
    return null;
  }

  const dialogTitle = mode === "edit" 
    ? `Editing ${currentItem?.name || existingItem?.name || endpoint.name.slice(0, -1)}` 
    : `Create New ${endpoint.name.slice(0, -1)}`;

  return (
    <Dialog
      open={open}
      onClose={!isSubmitting ? onClose : undefined}
      maxWidth={isGeoEntity ? "lg" : "sm"}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          overflow: 'hidden',
          '& *': {
            boxSizing: 'border-box'
          }
        }
      }}
    >
      {/* Header with AppBar */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {dialogTitle}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

            <DialogContent sx={{ p: 2, overflow: 'hidden' }}>
        <Grid container sx={{ width: '100%', maxWidth: '100%' }}>
          {/* Form Content */}
          <Grid item xs={isGeoEntity ? 6 : 12} sx={{ 
            pr: isGeoEntity ? 2 : 0, 
            minWidth: 0,
            maxWidth: isGeoEntity ? '50%' : '100%',
            overflow: 'hidden'
          }}>
            <Stack spacing={3}>
              {/* Basic Information */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Basic Information
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    fullWidth
                    required
                    autoFocus
                    disabled={isSubmitting}
                  />
                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                    disabled={isSubmitting}
                  />
                </Stack>
              </Box>

              {/* Relationships */}
              {mode === "edit" && currentItem && (
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">
                      Relationships
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={handleEditRelationships}
                      size="small"
                    >
                      Edit Relationships
                    </Button>
                  </Stack>
                  <Paper variant="outlined" sx={{ p: 2, minHeight: 60, bgcolor: "grey.50" }}>
                    {renderRelationships()}
                  </Paper>
                </Box>
              )}

              {/* Media */}
              {(() => {
                // Performance logs reduced for cleaner output - major optimizations already applied
                // Original detailed logging removed to reduce noise from duplicate renders
                return null;
              })()}
              {mode === "edit" && currentItem && relatedMedia.length > 0 && (
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">
                      Media ({relatedMedia.length} items)
                    </Typography>
                    {selectedPrimaryImage && (
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          onClick={handleSetPrimaryImage}
                          size="small"
                          color="primary"
                        >
                          Mark as Primary
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => prepareUnlink(parseInt(selectedPrimaryImage))}
                          size="small"
                          color="warning"
                          startIcon={<LinkIcon />}
                        >
                          Unlink
                        </Button>
                        {hasWritePermissions() && (
                          <Button
                            variant="outlined"
                            onClick={() => prepareDelete(parseInt(selectedPrimaryImage))}
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                          >
                            Delete
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Stack>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: "grey.50",
                      minHeight: "190px", // Reduced from 250px - just enough for carousel + padding
                    }}
                  >
                    {(() => {
                      console.log('[ItemFormDialog] About to render ImageCarousel with:', {
                        mediaItemsLength: relatedMedia.slice(0, displayedMediaCount).length,
                        selectedPrimaryImage,
                        displayedMediaCount,
                        mediaItems: relatedMedia.slice(0, displayedMediaCount).map(item => ({
                          id: item.id,
                          name: item.entity_name || item.name,
                          entity_thumbnail: item.entity_thumbnail,
                          hasThumbnail: !!item.entity_thumbnail
                        }))
                      });
                      return null;
                    })()}
                    <ImageCarousel
                      mediaItems={relatedMedia.slice(0, displayedMediaCount)}
                      onSelectImage={handleSelectImage}
                      selectedItemId={selectedPrimaryImage ? parseInt(selectedPrimaryImage) : null}
                      disablePreview={false}
                    />
                    {displayedMediaCount < relatedMedia.length && (
                      <Box textAlign="center" mt={2}>
                        <Button
                          variant="text"
                          onClick={handleLoadMoreMedia}
                          size="small"
                        >
                          Load More ({relatedMedia.length - displayedMediaCount} remaining)
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Geometry (Location/Area/Route) */}
              {(endpoint.tableName === "places" || endpoint.tableName === "family-areas" || endpoint.tableName === "trips") && (
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">
                      {endpoint.tableName === "places" ? "Location" : 
                       endpoint.tableName === "family-areas" ? "Area" : 
                       endpoint.tableName === "trips" ? "Route" : "Geometry"}
                    </Typography>
                    {(selectedCoordinates || selectedPolygon || formData.geometry) && (
                      <Button
                        variant="outlined"
                        onClick={handleClearCoordinates}
                        size="small"
                      >
                        Clear Geometry
                      </Button>
                    )}
                  </Stack>
                  
                  <Stack spacing={2}>
                    {/* Geometry Status Display */}
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                      {endpoint.tableName === "places" && selectedCoordinates ? (
                        <Typography variant="body2" fontFamily="monospace" color="success.main">
                          <strong>Coordinates:</strong> {selectedCoordinates[1].toFixed(6)}, {selectedCoordinates[0].toFixed(6)}
                        </Typography>
                      ) : endpoint.tableName === "family-areas" && selectedPolygon ? (
                        <Typography variant="body2" fontFamily="monospace" color="success.main">
                          <strong>{getGeometryDisplayName(selectedPolygon)}:</strong> {getGeometryDescription(selectedPolygon)}
                        </Typography>
                      ) : endpoint.tableName === "trips" && selectedPolygon ? (
                        <Typography variant="body2" fontFamily="monospace" color="success.main">
                          <strong>Route ({getGeometryDisplayName(selectedPolygon)}):</strong> {getGeometryDescription(selectedPolygon)}
                        </Typography>
                      ) : formData.geometry ? (
                        <Typography variant="body2" fontFamily="monospace" color="success.main">
                          <strong>Geometry:</strong> {(() => {
                            try {
                              const geom = JSON.parse(formData.geometry);
                              return `${geom.type} defined`;
                            } catch {
                              return "Custom geometry";
                            }
                          })()}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          {endpoint.tableName === "places" 
                            ? "Click on map to set location or edit GeoJSON below"
                            : "Edit GeoJSON below or use external tools"
                          }
                        </Typography>
                      )}
                    </Paper>

                    {/* Advanced GeoJSON Editor */}
                    <Accordion sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                          backgroundColor: 'action.hover',
                          '& .MuiAccordionSummary-content': {
                            alignItems: 'center'
                          }
                        }}
                      >
                        <Typography variant="subtitle2">
                          Advanced GeoJSON Editor
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (For complex geometries, Features, FeatureCollections)
                          </Typography>
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TextField
                          value={formData.geometry}
                          onChange={(e) => handleInputChange("geometry", e.target.value)}
                          fullWidth
                          multiline
                          rows={8}
                          disabled={isSubmitting}
                          placeholder={`Examples:
Point: {
  "type": "Point",
  "coordinates": [longitude, latitude]
}

Feature: {
  "type": "Feature",
  "geometry": { "type": "Point", "coordinates": [lng, lat] },
  "properties": { "name": "My Place" }
}

FeatureCollection: {
  "type": "FeatureCollection", 
  "features": [
    { "type": "Feature", "geometry": {...}, "properties": {...} }
  ]
}

LineString: {
  "type": "LineString", 
  "coordinates": [[lng,lat], [lng,lat], ...]
}

Polygon: {
  "type": "Polygon",
  "coordinates": [[[lng,lat], [lng,lat], ...]]
}

MultiLineString: {
  "type": "MultiLineString",
  "coordinates": [[[lng,lat], [lng,lat]], [[lng,lat], [lng,lat]]]
}

MultiPolygon: {
  "type": "MultiPolygon", 
  "coordinates": [[[[lng,lat], [lng,lat], ...]]]
}`}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                            }
                          }}
                          helperText={(() => {
                            if (!formData.geometry) return "Supports 2D/3D geometries, Features, and FeatureCollections (Z values auto-stripped)";
                            try {
                              const geometry = JSON.parse(formData.geometry);
                              
                              // Handle FeatureCollection
                              if (geometry.type === 'FeatureCollection') {
                                if (geometry.features && Array.isArray(geometry.features)) {
                                  const featureCount = geometry.features.length;
                                  if (featureCount === 1) {
                                    return `✓ FeatureCollection with 1 feature (will extract ${geometry.features[0].geometry?.type || 'unknown'}, Z-stripped)`;
                                  } else if (featureCount > 1) {
                                    // Check if we can combine into a Multi* type
                                    const allTypes = geometry.features.map((f: any) => f.geometry?.type).filter((type: any): type is string => typeof type === 'string');
                                    const geometryTypes = [...new Set(allTypes)];
                                    if (geometryTypes.length === 1) {
                                      const singleType = geometryTypes[0] as string;
                                      if (['LineString', 'Polygon', 'Point'].includes(singleType)) {
                                        return `✓ FeatureCollection with ${featureCount} ${singleType}s (will combine as Multi${singleType}, Z-stripped)`;
                                      } else if (['MultiLineString', 'MultiPolygon', 'MultiPoint'].includes(singleType)) {
                                        return `✓ FeatureCollection with ${featureCount} ${singleType}s (will flatten into single ${singleType}, Z-stripped)`;
                                      }
                                    }
                                    return `✓ FeatureCollection with ${featureCount} features (will combine as GeometryCollection, Z-stripped)`;
                                  } else {
                                    return "⚠ FeatureCollection is empty";
                                  }
                                } else {
                                  return "⚠ FeatureCollection missing 'features' array";
                                }
                              }
                              
                              // Handle Feature
                              if (geometry.type === 'Feature') {
                                if (geometry.geometry && geometry.geometry.type) {
                                  return `✓ Valid Feature containing ${geometry.geometry.type}`;
                                } else {
                                  return "⚠ Feature missing 'geometry' property";
                                }
                              }
                              
                              // Handle basic geometries
                              if (geometry.type && geometry.coordinates) {
                                const supportedTypes = ['Point', 'LineString', 'Polygon', 'MultiLineString', 'MultiPolygon'];
                                if (supportedTypes.includes(geometry.type)) {
                                  return `✓ Valid ${geometry.type} geometry`;
                                } else {
                                  return `⚠ Unsupported geometry type: ${geometry.type}`;
                                }
                              } else {
                                return "⚠ Missing 'type' or 'coordinates' property";
                              }
                            } catch {
                              return "⚠ Invalid JSON format";
                            }
                          })()}
                          error={(() => {
                            if (!formData.geometry) return false;
                            try {
                              const geometry = JSON.parse(formData.geometry);
                              
                              // Valid FeatureCollection
                              if (geometry.type === 'FeatureCollection') {
                                return !(geometry.features && Array.isArray(geometry.features));
                              }
                              
                              // Valid Feature
                              if (geometry.type === 'Feature') {
                                return !(geometry.geometry && geometry.geometry.type);
                              }
                              
                              // Valid basic geometry
                              if (!geometry.type || !geometry.coordinates) return true;
                              const supportedTypes = ['Point', 'LineString', 'Polygon', 'MultiLineString', 'MultiPolygon'];
                              return !supportedTypes.includes(geometry.type);
                            } catch {
                              return true;
                            }
                          })()}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Grid>

          {/* Map */}
          {(endpoint.tableName === "places" || endpoint.tableName === "family-areas" || endpoint.tableName === "trips") && (
            <Grid item xs={6} sx={{ minWidth: 0, maxWidth: '50%' }}>
              <Box 
                height="500px" 
                width="100%" 
                position="relative" 
                sx={{ 
                  overflow: 'hidden',
                  '& .maplibregl-map': {
                    width: '100% !important',
                    maxWidth: '100% !important'
                  },
                  '& .maplibregl-canvas': {
                    maxWidth: '100% !important'
                  }
                }}
              >
                <PreviewMap
                  points={mapData.points}
                  areas={mapData.areas}
                  autoZoom={shouldAutoZoom}
                  mapStyle="satellite"
                  onClick={endpoint.tableName === "places" ? handleMapClick : undefined}
                  initialViewState={initialViewState}
                  showLabels={false}
                />
                
                {/* Coordinate Search - only for places */}
                {endpoint.tableName === "places" && (
                  <Box
                    position="absolute"
                    top={8}
                    left={8}
                    right={8}
                    zIndex={1000}
                    display="flex"
                    gap={1}
                    sx={{
                      maxWidth: 'calc(100% - 16px)',
                      width: 'auto'
                    }}
                  >
                    <TextField
                      value={mapSearchTerm}
                      onChange={(e) => setMapSearchTerm(e.target.value)}
                      placeholder="lat, lng"
                      size="small"
                      sx={{
                        backgroundColor: 'white',
                        flex: 1,
                        minWidth: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && mapSearchTerm.trim()) {
                          handleCoordinateSearch(mapSearchTerm.trim());
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (mapSearchTerm.trim()) {
                          handleCoordinateSearch(mapSearchTerm.trim());
                        }
                      }}
                      disabled={!mapSearchTerm.trim()}
                      size="small"
                      sx={{ flexShrink: 0 }}
                    >
                      GO
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions sx={{ justifyContent: 'flex-end', px: 2, py: 1 }}>
        <Stack direction="row" spacing={2}>
          {mode === "edit" && hasWritePermissions() && (
            <Button
              onClick={() => setShowDeleteDialog(true)}
              color="error"
              startIcon={<DeleteIcon />}
              disabled={isSubmitting || isDeleting}
            >
              Delete
            </Button>
          )}
          <Button 
            onClick={handleCancel} 
            disabled={isSubmitting || isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting || isDeleting || !formData.name.trim()}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting 
              ? (mode === "edit" ? "Updating..." : "Creating...")
              : (mode === "edit" ? "Update" : "Create")
            }
          </Button>
        </Stack>
      </DialogActions>

      {/* RelationshipEditDialog for Edit Relationships */}
      {showEditRelationships && currentItem && endpoint && (
        <RelationshipEditDialog
          open={showEditRelationships}
          onClose={() => {
            setIsInRelationshipUpdate(false);
            setShowEditRelationships(false);
            // Don't reset hasLocalMediaChanges here - user might have deleted media before opening relationships dialog
          }}
          item={currentItem}
          endpoint={endpoint}
          onSave={(updatedItem) => {
            setShowEditRelationships(false);
            // Preserve local geometry and form changes when updating relationships
            const mergedItem = {
              ...updatedItem,
              // Preserve locally modified geometry if it exists
              ...(selectedCoordinates && isGeoEntity ? {
                geometry: {
                  type: "Point",
                  coordinates: selectedCoordinates
                }
              } : {}),
              // Preserve locally modified form data
              name: formData.name || updatedItem.name,
              description: formData.description || updatedItem.description,
            };
            
            setCurrentItem(mergedItem);
            
            // Clear the relationship update flag after successful save
            setIsInRelationshipUpdate(false);
            
            // Also clear media changes flag as relationships may have been updated
            setHasLocalMediaChanges(false);
            
            // Notify parent with updated item
            if (onItemUpdated) {
              onItemUpdated(updatedItem);
            }
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && currentItem && (
        <Dialog
          open={showDeleteDialog}
          onClose={() => !isDeleting && setShowDeleteDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ color: "error.main" }}>
            ⚠️ Permanently Delete {currentItem.name}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Warning:</strong> You are about to permanently delete this item from the database.
            </Typography>

            {/* Show relationships if they exist */}
            {currentItem.relationships && hasRelationships(currentItem.relationships) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="error" gutterBottom>
                  This item is currently linked to other items:
                </Typography>
                <Typography variant="body2" color="error">
                  All of these relationships will be permanently deleted.
                </Typography>
              </Box>
            )}

            <Typography
              variant="body1"
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "error.light",
                color: "error.contrastText",
                borderRadius: 1,
              }}
            >
              This action cannot be undone. Are you absolutely sure you want to delete this item?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setShowDeleteDialog(false)} 
              disabled={isDeleting}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Permanently Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Media Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ color: "error.main" }}>
          ⚠️ Permanently Delete Media
        </DialogTitle>
        <DialogContent>
          <Box component="div">
            <Typography variant="body1" component="div" sx={{ mb: 2 }}>
              <strong>Warning:</strong> You are about to permanently delete this
              media item from the database.
            </Typography>

            {(() => {
              // Collect all relationships from the media item
              const allRelationships: RelatedEntityItem[] = [];
              
              if (deleteMediaItem?.relationships) {
                // Add all related_entities relationships
                if (deleteMediaItem.relationships.related_entities) {
                  Object.entries(deleteMediaItem.relationships.related_entities).forEach(([key, entities]) => {
                    allRelationships.push(...(entities as RelatedEntityItem[]));
                  });
                }
                
                // Add all geo_entities relationships
                if (deleteMediaItem.relationships.geo_entities) {
                  Object.entries(deleteMediaItem.relationships.geo_entities).forEach(([key, entities]) => {
                    allRelationships.push(...(entities as RelatedEntityItem[]));
                  });
                }
                
                // Add media relationships if they exist
                if (deleteMediaItem.relationships.media && Array.isArray(deleteMediaItem.relationships.media)) {
                  allRelationships.push(...deleteMediaItem.relationships.media);
                }
              }
              
              // Performance: Delete dialog logging reduced to minimize duplicate output
              
              return allRelationships.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    color="error"
                    gutterBottom
                    component="div"
                  >
                    This media item is currently linked to:
                  </Typography>
                  {allRelationships.map((item, index) => (
                    <Typography
                      key={`delete-${item.id}-${index}`}
                      variant="body2"
                      sx={{ ml: 2 }}
                      component="div"
                    >
                      • {item.entity_name || item.name || `ID: ${item.id}`}
                    </Typography>
                  ))}
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ mt: 2 }}
                    component="div"
                  >
                    All of these relationships will be permanently deleted.
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                  This media item has no current relationships.
                </Typography>
              );
            })()}

            <Typography
              variant="body1"
              component="div"
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "error.light",
                color: "error.contrastText",
                borderRadius: 1,
              }}
            >
              This action cannot be undone. Are you absolutely sure you want to
              delete this media item?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteMedia}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Media Unlink Confirmation Dialog */}
      <Dialog
        open={unlinkDialogOpen}
        onClose={() => setUnlinkDialogOpen(false)}
      >
        <DialogTitle>Remove Link</DialogTitle>
        <DialogContent>
          <Box component="div">
            <Typography variant="body1" component="div">
              You are about to update the relationships for this media item and{" "}
              {currentItem?.name}.
            </Typography>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography
                variant="subtitle1"
                color="primary"
                gutterBottom
                component="div"
              >
                Current relationships:
              </Typography>
              {dialogRelationships?.current.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ ml: 2, fontStyle: 'italic' }}
                  component="div"
                  color="text.secondary"
                >
                  No relationships found
                </Typography>
              ) : (
                dialogRelationships?.current.map((r: RelatedEntityItem, index: number) => (
                  <Typography
                    key={`current-${r.id}-${index}`}
                    variant="body2"
                    sx={{ ml: 2 }}
                    component="div"
                  >
                    • {r.entity_name || r.name || `ID: ${r.id}`}
                  </Typography>
                ))
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle1"
                color="primary"
                gutterBottom
                component="div"
              >
                After removing link:
              </Typography>
              {dialogRelationships?.updated.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ ml: 2, fontStyle: 'italic' }}
                  component="div"
                  color="text.secondary"
                >
                  No relationships will remain
                </Typography>
              ) : (
                dialogRelationships?.updated.map((r: RelatedEntityItem, index: number) => (
                  <Typography
                    key={`updated-${r.id}-${index}`}
                    variant="body2"
                    sx={{ ml: 2 }}
                    component="div"
                  >
                    • {r.entity_name || r.name || `ID: ${r.id}`}
                  </Typography>
                ))
              )}
            </Box>

            <Typography variant="body1" component="div">
              Do you want to proceed with these changes?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUnlinkMedia} color="warning">
            Remove Link
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
});

export default ItemFormDialog; 